import _ from "lodash";
import React from "react";
import moment from "moment"
import styled from "styled-components";
import classNames from "classnames";
import WidgetSkeleton from "./WidgetSkeleton";
import WidgetDataLabel from "./WidgetDataLabel";
import WidgetMessage, { BaseWidgetMessages } from "./WidgetMessage";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Card, Col, Overlay, Popover } from "react-bootstrap"
import { ThemedSpan } from "../../../components/utilities";
import { useMeetingsSummaryData } from "../hooks/MeetingsSummaryContext";
import { toCommas } from "../../../helpers/numbers";

// registers the ability to use charts here
ChartJS.register(ArcElement, Tooltip, Legend);

const WidgetBodyYearToDatePopover = styled(Popover)`
    max-width: 500px;
`;

const WidgetBodyYearToDateGroupingDisplay = ({ dataArray = [] }) => {
    // this will build the chart data!
    const dataset = React.useMemo(() => {
        if (_.isEmpty(dataArray)) {
            return null;
        }

        const total = _.sum(dataArray.map(el => el.total));
        const dataset = dataArray.map(el => {
            let pct = (el.total / total) * 100;
            return {
                label: `${el.styleText} (${pct.toFixed(0)}%)`,
                labelStyle: el.styleColour,
                total: el.total,
                pct
            };
        });

        return {
            labels: dataset.map(el => el.label),
            datasets: [{
                label: '# of appointments attended',
                data: dataset.map(el => el.total),
                backgroundColor: dataset.map(el => el.labelStyle),
                borderColor: dataset.map(el => el.labelStyle),
                borderWidth: 1
            }]
        };
    }, [ dataArray ]);

    if (!dataset) {
        return <WidgetMessage message="Could not load appointment grouping data." />
    }

    return <Pie data={dataset} />
};

const WidgetBodyYearToDateContainer = ({ data, label = "Attended since January" }) => {
    const ref = React.useRef();
    const [isShown, setIsShown] = React.useState(false);
    return (
        <div className="col-12 mt-4">
            <div className="row">
                <div className="col-6">
                    <ThemedSpan variant="primary">{label}</ThemedSpan>
                </div>
                <div className="col-6 text-end fw-bold">
                    {data.attendedThisYear > 0 && (
                        <>
                            <ThemedSpan 
                                ref={ref} 
                                variant="primary" 
                                className={classNames({ "text-decoration-underline": data.attendedThisYear > 0 })}
                                onMouseEnter={() => setIsShown(() => true)} 
                                onMouseLeave={() => setIsShown(() => false)}
                            >
                                {data.attendedThisYear > 0 ? toCommas(data.attendedThisYear) : '-'}
                            </ThemedSpan>
                            <Overlay show={isShown} placement="left" target={ref}>
                                <WidgetBodyYearToDatePopover 
                                    style={{ width: '400px' }}
                                    onMouseEnter={() => setIsShown(() => true)} 
                                    onMouseLeave={() => setIsShown(() => false)}
                                >
                                    <Popover.Header>Appointments Attended</Popover.Header>
                                    <Popover.Body className="text-left"> 
                                        <WidgetBodyYearToDateGroupingDisplay dataArray={data.appointmentSummaryGroupings} />
                                    </Popover.Body>
                                </WidgetBodyYearToDatePopover>
                            </Overlay>
                        </>
                    )}
                    {(data.attendedThisYear === 0) && (
                        <ThemedSpan variant="primary">-</ThemedSpan>
                    )}   
                </div>
            </div>
        </div>
    );
}

const WidgetBody = () => {
    const formattedDate = moment().format('MMMM YYYY');
    const { data, error } = useMeetingsSummaryData();
    
    if (error) {
        return <WidgetMessage icon={['fas', 'exclamation']} message={BaseWidgetMessages.Error} />
    }

    if (!data || data.length < 1) {
        return <WidgetMessage message={BaseWidgetMessages.NoResult} />
    }

    return (
        <>
            <WidgetDataLabel label={`Attended in ${formattedDate}`} value={toCommas(data.attendedThisMonth)} />
            <WidgetDataLabel label={`Scheduled for ${formattedDate}`} value={toCommas(data.scheduledThisMonth)} />
            <WidgetDataLabel label={`Total Scheduled`} value={toCommas(data.totalScheduled)} />
            <WidgetBodyYearToDateContainer data={data} />
            <WidgetDataLabel label={`Minimum projected by year end`} value={toCommas(data.totalMeetingsProjectedForYear)} />
        </>
    )
}

export const MeetingsSummaryWidget = () => {
    const { isFetching, isLoading } = useMeetingsSummaryData();

    if (isFetching || isLoading) {
        return <WidgetSkeleton />
    }

    return (
        <Col xs={12} sm={12} md={6} lg={6} xl={4}>
            <a className="text-reset text-decoration-none" href={"/personal/reminders"}>
                <Card className="overflow-hidden" style={{ height: "300px", color: "dodgerblue" }}>
                    <Card.Body>
                        <Card.Title className="pb-2" style={{ fontSize: "18px" }}>
                            <ThemedSpan variant="primary" className="fw-bold">Meetings</ThemedSpan>
                        </Card.Title>
                        <Card.Text as="div" className="row g-2">
                            <WidgetBody />
                        </Card.Text>
                    </Card.Body>
                </Card>
            </a>
        </Col>
    );
}